import type { ReactNode } from 'react'
import { useRef } from 'react'
import type { FloatProps } from '@headlessui-float/react'
import { twMerge } from 'tailwind-merge'
import { HoverOverlay, HoverOverlayArrow } from 'shared/components/hover-overlay'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import { useIsOverflowText } from 'shared/hooks/use-is-overflow-text'
import useWindowSize from 'shared/hooks/use-window-size'

export const Tooltip = ({
  children,
  mode,
  label,
  position,
  className,
  wrapperClassName,
  tooltipClassName,
}: {
  children: ReactNode
  mode: 'click' | 'hover' | 'overflow'
  label: ReactNode | string
  position?: FloatProps['placement']
  className?: string
  wrapperClassName?: string
  tooltipClassName?: string
}): ReactNode => {
  const targetRef = useRef(null)
  const { width } = useWindowSize()
  const { show, delayOpen, delayClose } = useFloatHover()

  const isOverflow = useIsOverflowText(targetRef)

  return (
    <HoverOverlay
      portal
      show={show}
      placement={position || 'top'}
      gap={16}
      className={wrapperClassName}
      floatProps={{
        offset: 16,
        flip: {
          fallbackPlacements: ['top', 'bottom', 'left', 'right'],
          fallbackStrategy: 'initialPlacement',
        },
        arrow: true,
        enter: 'transition duration-100 ease-out',
        enterFrom: 'opacity-0',
        enterTo: 'opacity-100',
        leave: 'transition duration-75 ease-in',
        leaveFrom: 'opacity-100',
        leaveTo: 'opacity-0',
      }}
    >
      <div
        ref={targetRef}
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
        className={twMerge(
          'relative flex items-center justify-center truncate',
          className ?? 'size-full',
          mode === 'overflow' && 'overflow-hidden',
        )}
      >
        {children}
      </div>
      <div
        data-testid="tooltip-panel"
        className={twMerge(
          'rounded-lg shadow-2xl',
          show ? 'z-50' : 'z-0',
          mode === 'overflow' && !isOverflow && 'invisible',
          width < 1024 && 'invisible',
          tooltipClassName,
        )}
      >
        <HoverOverlayArrow className="absolute size-5 rotate-45 border border-background-secondary bg-background-secondary" />
        <div
          className={`relative w-fit rounded-lg bg-background-secondary px-5 py-3 whitespace-normal`}
        >
          <div className="w-full text-center font-avertaPE text-sm font-medium break-normal text-text-tertiary">
            {label}
          </div>
        </div>
      </div>
    </HoverOverlay>
  )
}
